import React, {createContext, useCallback, useContext, useMemo} from 'react'
import {ToastProvider, useToast} from '~/src/design-system/BaseComponents'
import type {ToastAction} from '~/src/design-system/BaseComponents'
import {extractErrorMessage} from '~/src/utils/extractErrorMessage'

/** Options for a success toast. */
export interface SnackbarOptions {
  /** A link-styled control after the message (DS Toast `action`): it runs and closes the toast. */
  action?: ToastAction
}

interface GlobalSnackbarContextType {
  /**
   * Show a success message (green)
   * @param message - The message to display
   * @param options - Optional: an `action` after the message (STFFLG-4184)
   */
  showSuccess: (message: string, options?: SnackbarOptions) => void

  /**
   * Show an error message (red)
   * Handles RTK Query errors, Error objects, or plain strings
   * @param error - Error object or string message
   */
  showError: (error: unknown) => void

  /**
   * Show a warning message (orange)
   * @param message - The message to display
   */
  showWarning: (message: string) => void

  /**
   * Show an info message (blue)
   * @param message - The message to display
   */
  showInfo: (message: string) => void
}

const GlobalSnackbarContext = createContext<GlobalSnackbarContextType | undefined>(undefined)

interface GlobalSnackbarProviderProps {
  children: React.ReactNode
  /**
   * Auto-hide duration in milliseconds (default: 5000)
   */
  autoHideDuration?: number
}

/**
 * Inner component that accesses DS Toast context
 */
function GlobalSnackbarInner({children}: {children: React.ReactNode}) {
  const {toast} = useToast()

  const showSuccess = useCallback(
    (message: string, options?: SnackbarOptions) => {
      toast({tone: 'success', title: message, ...(options?.action && {action: options.action})})
    },
    [toast],
  )

  const showError = useCallback(
    (error: unknown) => {
      const message = extractErrorMessage(error)
      toast({tone: 'error', title: message})
    },
    [toast],
  )

  const showWarning = useCallback(
    (message: string) => {
      toast({tone: 'warning', title: message})
    },
    [toast],
  )

  const showInfo = useCallback(
    (message: string) => {
      toast({tone: 'info', title: message})
    },
    [toast],
  )

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue: GlobalSnackbarContextType = useMemo(
    () => ({
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [showSuccess, showError, showWarning, showInfo],
  )

  return (
    <GlobalSnackbarContext.Provider value={contextValue}>{children}</GlobalSnackbarContext.Provider>
  )
}

export function GlobalSnackbarProvider({children}: GlobalSnackbarProviderProps) {
  return (
    <ToastProvider>
      <GlobalSnackbarInner>{children}</GlobalSnackbarInner>
    </ToastProvider>
  )
}

/**
 * Hook to access global snackbar functions
 *
 * @example
 * ```tsx
 * const { showSuccess, showError } = useGlobalSnackbar()
 *
 * // In a try/catch block
 * try {
 *   await createJobOrder({...}).unwrap()
 *   showSuccess('Job Order created successfully!')
 *   navigate('/job-orders')
 * } catch (error) {
 *   showError(error)
 * }
 * ```
 */
export function useGlobalSnackbar(): GlobalSnackbarContextType {
  const context = useContext(GlobalSnackbarContext)

  if (!context) {
    throw new Error('useGlobalSnackbar must be used within a GlobalSnackbarProvider')
  }

  return context
}
